import { useEffect, useMemo, useState } from "react";
import {
  Loader2, FolderOpen, FileText, FileBadge, IdCard, ReceiptText, FileSpreadsheet, ExternalLink, Search,
} from "lucide-react";
import { api, API, getToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const TYPE_META = {
  report: { label: "Credit Report", icon: FileText, color: "#60A5FA" },
  credit_report: { label: "Credit Report", icon: FileText, color: "#60A5FA" },
  ssn_card: { label: "SSN Card", icon: FileBadge, color: "#C1121F" },
  gov_id: { label: "Government ID", icon: IdCard, color: "#15803D" },
  utility_bill: { label: "Utility Bill", icon: ReceiptText, color: "#F59E0B" },
  other: { label: "Other", icon: FileSpreadsheet, color: "#94A3B8" },
};

const fmtSize = (b) => (!b ? "" : b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`);

export default function DocumentLibrary() {
  const [items, setItems] = useState(null);
  const [consumer, setConsumer] = useState("all");
  const [kind, setKind] = useState("all");
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/library").then(({ data }) => setItems(data)).catch(() => setItems([])); }, []);

  const consumers = useMemo(() => {
    const m = new Map();
    (items || []).forEach((i) => { if (i.consumerId) m.set(i.consumerId, i.consumerName || "Unknown"); });
    return [...m.entries()];
  }, [items]);

  const view = (path) => window.open(`${API}/files/${path}?auth=${getToken()}`, "_blank");

  if (!items) return <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-[#C1121F]" /></div>;

  const filtered = items.filter((i) =>
    (consumer === "all" || i.consumerId === consumer) &&
    (kind === "all" || (kind === "report" ? (i.kind === "report" || i.docType === "credit_report") : i.docType === kind)) &&
    (!search || (i.original_filename || "").toLowerCase().includes(search.toLowerCase()) || (i.consumerName || "").toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="space-y-6" data-testid="documents-panel">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search files or consumers…" className="pl-9 bg-[#FFFFFF] border-neutral-200" data-testid="library-search-input" />
        </div>
        <Select value={consumer} onValueChange={setConsumer}>
          <SelectTrigger className="w-52 bg-[#FFFFFF] border-neutral-200" data-testid="library-consumer-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All consumers</SelectItem>
            {consumers.map(([id, name]) => <SelectItem key={id} value={id}>{name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={kind} onValueChange={setKind}>
          <SelectTrigger className="w-48 bg-[#FFFFFF] border-neutral-200" data-testid="library-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            <SelectItem value="report">Credit Report</SelectItem>
            <SelectItem value="ssn_card">SSN Card</SelectItem>
            <SelectItem value="gov_id">Government ID</SelectItem>
            <SelectItem value="utility_bill">Utility Bill</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="text-sm text-neutral-500">{filtered.length} file{filtered.length === 1 ? "" : "s"}</div>

      {filtered.length === 0 ? (
        <div className="text-center py-20 text-neutral-400"><FolderOpen className="h-10 w-10 mx-auto opacity-40 mb-3" />No documents match these filters.</div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((i) => {
            const meta = TYPE_META[i.docType] || TYPE_META.other;
            return (
              <div key={`${i.kind}-${i.id}`} className="group rounded-2xl border border-neutral-200 bg-[#FFFFFF]/80 p-5 hover:border-[#C1121F]/40 transition-colors" data-testid="library-item">
                <div className="flex items-start justify-between">
                  <span className="inline-grid place-items-center h-11 w-11 rounded-xl" style={{ backgroundColor: `${meta.color}1f` }}>
                    <meta.icon className="h-5 w-5" style={{ color: meta.color }} />
                  </span>
                  <button onClick={() => view(i.storage_path)} className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-[#9B0E18] transition-opacity" title="Open file" data-testid="library-view-button">
                    <ExternalLink className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-4 text-sm font-medium truncate" title={i.original_filename}>{i.original_filename}</div>
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <span className="px-2 py-0.5 rounded-md border" style={{ borderColor: `${meta.color}55`, color: meta.color }}>{meta.label}</span>
                  {i.kind === "report" && i.accountsCount > 0 && <span className="text-neutral-400">{i.accountsCount} items</span>}
                  {i.uploaded_by === "consumer" && <span className="text-emerald-700/80">by client</span>}
                </div>
                <div className="mt-3 flex items-center justify-between text-xs text-neutral-400">
                  <span className="truncate">{i.consumerName || "—"}</span>
                  <span>{i.created_at ? new Date(i.created_at).toLocaleDateString() : ""}{i.size ? ` · ${fmtSize(i.size)}` : ""}</span>
                </div>
                <button onClick={() => view(i.storage_path)} className="mt-3 w-full text-center text-xs text-[#C1121F] hover:text-[#9B0E18] py-1.5 rounded-lg bg-neutral-50 border border-neutral-200" data-testid="library-open-button">View document</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
